import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { TextArea } from '../components/ui/TextArea';
import { Table } from '../components/ui/Table';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Checkbox } from '../components/ui/Checkbox';
import { TermsAndConditions } from '../types';
import { formatDate } from '../utils/calculations';
import { callBillingAPI } from '../utils/api';
import { SETTINGS_RESET_EVENT } from '../components/layout/Layout';
import { BuildingIcon, PlusIcon, EditIcon, TrashIcon, FileTextIcon, AlertCircleIcon, PercentIcon, ArrowLeftIcon, MailIcon, SettingsIcon, RepeatIcon } from 'lucide-react';
import { EditCompanyGeneral, CompanyData } from './settings/EditCompanyGeneral';
import { EditCompanyTemplates } from './settings/EditCompanyTemplates';
import { EditCompanySubscriptions } from './settings/EditCompanySubscriptions';
type EditCompanyTab = 'general' | 'templates' | 'subscription_templates';
export function Settings() {
  const location = useLocation();
  const {
    selectedCompanyId,
    getTermsByCompanyId,
    createTerms,
    updateTerms,
    deleteTerms,
    refreshCompanies
  } = useData();
  // Company list state
  const [companies, setCompanies] = useState<CompanyData[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);
  // Terms state
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [editingTerms, setEditingTerms] = useState<TermsAndConditions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [termsName, setTermsName] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [termsErrors, setTermsErrors] = useState<Record<string, string>>({});
  const companyTerms = getTermsByCompanyId(selectedCompanyId);
  // Edit company state
  const [editingCompany, setEditingCompany] = useState<CompanyData | null>(null);
  const [activeTab, setActiveTab] = useState<EditCompanyTab>('general');
  const [companyName, setCompanyName] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');
  const [companyStripePublicKey, setCompanyStripePublicKey] = useState('');
  const [companyStripePrivateKey, setCompanyStripePrivateKey] = useState('');
  const [companyTaxRate, setCompanyTaxRate] = useState<number>(0);
  const [enterNewPrivateKey, setEnterNewPrivateKey] = useState(false);
  const [companyErrors, setCompanyErrors] = useState<Record<string, string>>({});
  const [isSavingCompany, setIsSavingCompany] = useState(false);
  const [isLoadingCompanyDetails, setIsLoadingCompanyDetails] = useState(false);
  const [companyDetailsError, setCompanyDetailsError] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [logoChanged, setLogoChanged] = useState(false);
  // Template state
  const [invoiceEmailTemplate, setInvoiceEmailTemplate] = useState('');
  const [invoiceEmailSubjectTemplate, setInvoiceEmailSubjectTemplate] = useState('');
  const [invoiceSmsTemplate, setInvoiceSmsTemplate] = useState('');
  const [otpEmailTemplate, setOtpEmailTemplate] = useState('');
  const [otpEmailSubjectTemplate, setOtpEmailSubjectTemplate] = useState('');
  const editorInitializedRef = useRef(false);
  // Default OTP templates
  const DEFAULT_OTP_EMAIL_TEMPLATE = 'Hello, <br/><br/>The One Time Password to verify your email address is <b>%code%</b>.';
  const DEFAULT_OTP_EMAIL_SUBJECT_TEMPLATE = 'OTP to verify your email address';
  const syncEditorContent = useCallback(() => {
    return invoiceEmailTemplate;
  }, [invoiceEmailTemplate]);
  // Fetch companies
  const fetchCompanies = async () => {
    setIsLoadingCompanies(true);
    setCompaniesError(null);
    try {
      const response = await callBillingAPI('getCompanies', {});
      let apiCompanies: any[] = [];
      if (Array.isArray(response)) apiCompanies = response;else if (response.companies && Array.isArray(response.companies)) apiCompanies = response.companies;else if (response.data && Array.isArray(response.data)) apiCompanies = response.data;else throw new Error('Invalid API response format');
      const transformedCompanies: CompanyData[] = apiCompanies.map(c => ({
        id: c.id,
        name: c.name,
        email: c.email || '',
        phone: c.phone || '',
        alias: c.alias,
        stripe_public_key: c.stripe_public_key || '',
        stripe_private_key: c.stripe_private_key || '',
        logo: c.logo || '',
        tax_rate: c.tax_rate ?? 0,
        invoice_email_template: c.invoice_email_template || '',
        invoice_email_subject_template: c.invoice_email_subject_template || '',
        invoice_sms_template: c.invoice_sms_template || '',
        otp_email_template: c.otp_email_template || '',
        otp_email_subject_template: c.otp_email_subject_template || ''
      }));
      setCompanies(transformedCompanies);
    } catch (error) {
      setCompaniesError(error instanceof Error ? error.message : 'Failed to load companies');
    } finally {
      setIsLoadingCompanies(false);
    }
  };
  useEffect(() => {
    fetchCompanies();
  }, []);
  // Reset events
  useEffect(() => {
    const handleResetToList = () => {
      setEditingCompany(null);
      setActiveTab('general');
      editorInitializedRef.current = false;
    };
    window.addEventListener(SETTINGS_RESET_EVENT, handleResetToList);
    return () => {
      window.removeEventListener(SETTINGS_RESET_EVENT, handleResetToList);
    };
  }, []);
  useEffect(() => {
    const state = location.state as {
      showCompanyList?: boolean;
      openSubscriptionTemplates?: boolean;
    } | null;
    if (state?.showCompanyList) {
      setEditingCompany(null);
      setActiveTab('general');
      editorInitializedRef.current = false;
      window.history.replaceState({}, document.title);
    }
    if (state?.openSubscriptionTemplates) {
      const matchingCompany = companies.find(c => c.id === selectedCompanyId);
      if (matchingCompany) {
        openEditCompany(matchingCompany);
        setActiveTab('subscription_templates');
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, companies, selectedCompanyId]);
  const openEditCompany = async (company: CompanyData) => {
    setEditingCompany(company);
    setActiveTab('general');
    setCompanyErrors({});
    setIsLoadingCompanyDetails(true);
    setCompanyDetailsError(null);
    editorInitializedRef.current = false;
    setCompanyName(company.name || '');
    setCompanyEmail(company.email || '');
    setCompanyPhone(company.phone || '');
    setCompanyStripePublicKey(company.stripe_public_key || '');
    setCompanyStripePrivateKey('');
    setCompanyTaxRate(company.tax_rate ?? 0);
    setEnterNewPrivateKey(false);
    setCompanyLogo(company.logo || null);
    setLogoChanged(false);
    setInvoiceEmailTemplate(company.invoice_email_template || '');
    setInvoiceEmailSubjectTemplate(company.invoice_email_subject_template || '');
    setInvoiceSmsTemplate(company.invoice_sms_template || '');
    setOtpEmailTemplate(company.otp_email_template || DEFAULT_OTP_EMAIL_TEMPLATE);
    setOtpEmailSubjectTemplate(company.otp_email_subject_template || DEFAULT_OTP_EMAIL_SUBJECT_TEMPLATE);
    try {
      const details = await callBillingAPI('getCompany', {
        id: company.id
      });
      setCompanyName(details.name || '');
      setCompanyEmail(details.email || '');
      setCompanyPhone(details.phone || '');
      setCompanyStripePublicKey(details.stripe_public_key || '');
      setCompanyTaxRate(details.tax_rate ?? 0);
      setCompanyLogo(details.logo || null);
      setInvoiceEmailTemplate(details.invoice_email_template || '');
      setInvoiceEmailSubjectTemplate(details.invoice_email_subject_template || '');
      setInvoiceSmsTemplate(details.invoice_sms_template || '');
      setOtpEmailTemplate(details.otp_email_template || DEFAULT_OTP_EMAIL_TEMPLATE);
      setOtpEmailSubjectTemplate(details.otp_email_subject_template || DEFAULT_OTP_EMAIL_SUBJECT_TEMPLATE);
      setEditingCompany({
        id: details.id || company.id,
        name: details.name || '',
        email: details.email || '',
        phone: details.phone || '',
        alias: details.alias || company.alias,
        stripe_public_key: details.stripe_public_key || '',
        stripe_private_key: '',
        logo: details.logo || '',
        tax_rate: details.tax_rate ?? 0,
        invoice_email_template: details.invoice_email_template || '',
        invoice_email_subject_template: details.invoice_email_subject_template || '',
        invoice_sms_template: details.invoice_sms_template || '',
        otp_email_template: details.otp_email_template || DEFAULT_OTP_EMAIL_TEMPLATE,
        otp_email_subject_template: details.otp_email_subject_template || DEFAULT_OTP_EMAIL_SUBJECT_TEMPLATE
      });
    } catch (error) {
      setCompanyDetailsError(error instanceof Error ? error.message : 'Failed to load company details');
    } finally {
      setIsLoadingCompanyDetails(false);
    }
  };
  const closeEditCompany = () => {
    setEditingCompany(null);
    setActiveTab('general');
    editorInitializedRef.current = false;
    setCompanyErrors({});
  };
  const validateCompanyForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!companyName || !companyName.trim()) errors.name = 'Company name is required';
    if (companyEmail && companyEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail.trim())) errors.email = 'Invalid email format';
    if (companyTaxRate < 0 || companyTaxRate > 100) errors.tax_rate = 'Tax rate must be between 0 and 100';
    setCompanyErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveCompany = async (overrides?: {
    invoiceEmailTemplate?: string;
    otpEmailTemplate?: string;
  }) => {
    if (!validateCompanyForm()) {
      setActiveTab('general');
      return;
    }
    if (!editingCompany) return;
    setIsSavingCompany(true);
    setCompanyErrors({});
    const currentEmailTemplate = overrides?.invoiceEmailTemplate ?? syncEditorContent();
    const currentOtpEmailTemplate = overrides?.otpEmailTemplate ?? otpEmailTemplate;
    const hasExistingPublicKey = !!editingCompany.stripe_public_key;
    const shouldSendPrivateKey = !hasExistingPublicKey || enterNewPrivateKey;
    try {
      const apiParams: Record<string, string | number> = {
        id: editingCompany.id,
        name: companyName,
        email: companyEmail || '',
        phone: companyPhone || '',
        stripe_public_key: companyStripePublicKey || '',
        tax_rate: Math.round(companyTaxRate),
        invoice_email_template: currentEmailTemplate || '',
        invoice_email_subject_template: invoiceEmailSubjectTemplate || '',
        invoice_sms_template: invoiceSmsTemplate || '',
        otp_email_template: currentOtpEmailTemplate || '',
        otp_email_subject_template: otpEmailSubjectTemplate || ''
      };
      if (shouldSendPrivateKey) apiParams.stripe_private_key = companyStripePrivateKey || '';
      if (logoChanged) apiParams.logo = companyLogo || '';
      await callBillingAPI('editCompany', apiParams);
      await fetchCompanies();
      await refreshCompanies();
      setEditingCompany(null);
      editorInitializedRef.current = false;
    } catch (error) {
      setCompanyErrors({
        submit: error instanceof Error ? error.message : 'Failed to save company'
      });
    } finally {
      setIsSavingCompany(false);
    }
  };
  // Terms handlers
  const openCreateTermsModal = () => {
    setEditingTerms(null);
    setTermsName('');
    setTermsContent('');
    setIsDefault(companyTerms.length === 0);
    setTermsErrors({});
    setIsTermsModalOpen(true);
  };
  const openEditTermsModal = (terms: TermsAndConditions) => {
    setEditingTerms(terms);
    setTermsName(terms.name);
    setTermsContent(terms.content);
    setIsDefault(terms.is_default);
    setTermsErrors({});
    setIsTermsModalOpen(true);
  };
  const validateTermsForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!termsName.trim()) errors.name = 'Name is required';
    if (!termsContent.trim()) errors.content = 'Content is required';
    setTermsErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveTerms = async () => {
    if (!validateTermsForm()) return;
    setIsLoading(true);
    try {
      if (editingTerms) await updateTerms(editingTerms.id, {
        name: termsName,
        content: termsContent,
        is_default: isDefault
      });else await createTerms({
        company_id: selectedCompanyId,
        name: termsName,
        content: termsContent,
        is_default: isDefault
      });
      setIsTermsModalOpen(false);
    } catch (error) {
      console.error('Failed to save terms:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteTerms = async (id: string) => {
    setIsLoading(true);
    try {
      await deleteTerms(id);
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Failed to delete terms:', error);
    } finally {
      setIsLoading(false);
    }
  };
  // Table columns
  const companyColumns = [{
    key: 'name',
    header: 'Company Name',
    render: (company: CompanyData) => <div className="flex items-center gap-3" data-id="element-2650">
          {company.logo ? <img src={`data:image/png;base64,${company.logo}`} alt={company.name} className="w-20 h-12 rounded-lg object-contain bg-gray-50 p-1" data-id="element-2651" /> : <div className="w-20 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-2652">
              <BuildingIcon className="w-6 h-6 text-blue-600" data-id="element-2653" />
            </div>}
          <span className="font-medium text-gray-900" data-id="element-2654">{company.name}</span>
        </div>
  }, {
    key: 'tax_rate',
    header: 'Tax Rate',
    render: (company: CompanyData) => <div className="flex items-center gap-1" data-id="element-2655">
          <PercentIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2656" />
          <span className="text-sm text-gray-700" data-id="element-2657">
            {company.tax_rate ?? 0}%
          </span>
        </div>
  }, {
    key: 'stripe_key',
    header: 'Stripe',
    render: (company: CompanyData) => company.stripe_public_key ? <span className="inline-flex items-center text-sm font-medium text-green-700" data-id="element-2658">
            Set
          </span> : <span className="inline-flex items-center text-sm font-medium text-red-600" data-id="element-2659">
            Not Set
          </span>
  }, {
    key: 'actions',
    header: '',
    render: (company: CompanyData) => <div className="flex items-center justify-end" data-id="element-2660">
          <Button variant="ghost" size="sm" onClick={() => openEditCompany(company)} aria-label="Edit company" data-id="element-2661">
            <EditIcon className="w-4 h-4" data-id="element-2662" />
          </Button>
        </div>
  }];
  const termsColumns = [{
    key: 'name',
    header: 'Name',
    render: (terms: TermsAndConditions) => <div className="flex items-center gap-2" data-id="element-2663">
          <FileTextIcon className="w-4 h-4 text-gray-400" data-id="element-2664" />
          <span className="font-medium text-gray-900" data-id="element-2665">{terms.name}</span>
          {terms.is_default && <Badge variant="info" className="ml-2" data-id="element-2666">
              Default
            </Badge>}
        </div>
  }, {
    key: 'updated',
    header: 'Last Updated',
    render: (terms: TermsAndConditions) => <span className="text-sm text-gray-500" data-id="element-2667">
          {formatDate(terms.updated_at)}
        </span>
  }, {
    key: 'actions',
    header: '',
    render: (terms: TermsAndConditions) => <div className="flex items-center justify-end gap-2" data-id="element-2668">
          <Button variant="ghost" size="sm" onClick={() => openEditTermsModal(terms)} aria-label="Edit terms" data-id="element-2669">
            <EditIcon className="w-4 h-4" data-id="element-2670" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(terms.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" aria-label="Delete terms" disabled={terms.is_default} data-id="element-2671">
            <TrashIcon className="w-4 h-4" data-id="element-2672" />
          </Button>
        </div>
  }];
  const TabButton = ({
    tab,
    label,
    icon
  }: {
    tab: EditCompanyTab;
    label: string;
    icon: React.ReactNode;
  }) => <button type="button" onClick={() => {
    if (activeTab === 'templates') syncEditorContent();
    setActiveTab(tab);
  }} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2673">
      {icon}
      {label}
    </button>;
  // Edit Company view
  if (editingCompany) {
    return <div className="space-y-6" data-id="element-2674">
        <div className="flex items-center gap-4" data-id="element-2675">
          <Button variant="ghost" size="sm" onClick={closeEditCompany} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-2677" />} data-id="element-2676">
            Back to Settings
          </Button>
        </div>
        <div data-id="element-2678">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2679">Edit Company</h1>
          <p className="text-gray-500 mt-1" data-id="element-2680">
            Update company information and settings
          </p>
        </div>
        <div className="border-b border-gray-200" data-id="element-2681">
          <div className="flex gap-4" data-id="element-2682">
            <TabButton tab="general" label="General Settings" icon={<SettingsIcon className="w-4 h-4" data-id="element-2684" />} data-id="element-2683" />
            <TabButton tab="templates" label="Email/SMS Templates" icon={<MailIcon className="w-4 h-4" data-id="element-2686" />} data-id="element-2685" />
            <TabButton tab="subscription_templates" label="Subscription Templates" icon={<RepeatIcon className="w-4 h-4" data-id="element-2688" />} data-id="element-2687" />
          </div>
        </div>

        {activeTab === 'general' && <EditCompanyGeneral editingCompany={editingCompany} isLoadingCompanyDetails={isLoadingCompanyDetails} companyDetailsError={companyDetailsError} companyName={companyName} setCompanyName={setCompanyName} companyEmail={companyEmail} setCompanyEmail={setCompanyEmail} companyPhone={companyPhone} setCompanyPhone={setCompanyPhone} companyTaxRate={companyTaxRate} setCompanyTaxRate={setCompanyTaxRate} companyStripePublicKey={companyStripePublicKey} setCompanyStripePublicKey={setCompanyStripePublicKey} companyStripePrivateKey={companyStripePrivateKey} setCompanyStripePrivateKey={setCompanyStripePrivateKey} enterNewPrivateKey={enterNewPrivateKey} setEnterNewPrivateKey={setEnterNewPrivateKey} companyLogo={companyLogo} setCompanyLogo={setCompanyLogo} logoChanged={logoChanged} setLogoChanged={setLogoChanged} companyErrors={companyErrors} setCompanyErrors={setCompanyErrors} isSavingCompany={isSavingCompany} handleSaveCompany={handleSaveCompany} closeEditCompany={closeEditCompany} openEditCompany={openEditCompany} data-id="element-2689" />}

        {activeTab === 'templates' && <EditCompanyTemplates invoiceEmailTemplate={invoiceEmailTemplate} setInvoiceEmailTemplate={setInvoiceEmailTemplate} invoiceEmailSubjectTemplate={invoiceEmailSubjectTemplate} setInvoiceEmailSubjectTemplate={setInvoiceEmailSubjectTemplate} invoiceSmsTemplate={invoiceSmsTemplate} setInvoiceSmsTemplate={setInvoiceSmsTemplate} otpEmailTemplate={otpEmailTemplate} setOtpEmailTemplate={setOtpEmailTemplate} otpEmailSubjectTemplate={otpEmailSubjectTemplate} setOtpEmailSubjectTemplate={setOtpEmailSubjectTemplate} companyErrors={companyErrors} isSavingCompany={isSavingCompany} handleSaveCompany={handleSaveCompany} closeEditCompany={closeEditCompany} syncEditorContent={syncEditorContent} editorInitializedRef={editorInitializedRef} data-id="element-2690" />}

        {activeTab === 'subscription_templates' && <EditCompanySubscriptions companyId={editingCompany.id} companyTaxRate={companyTaxRate} selectedCompanyId={selectedCompanyId} data-id="element-2691" />}
      </div>;
  }
  // Main settings list view
  return <div className="space-y-6" data-id="element-2692">
      <div data-id="element-2693">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2694">Settings</h1>
        <p className="text-gray-500 mt-1" data-id="element-2695">
          Manage your billing system configuration
        </p>
      </div>

      <Card padding="none" data-id="element-2696">
        <div className="p-6 border-b border-gray-200" data-id="element-2697">
          <CardHeader title="Companies" subtitle="Manage company information, tax rates, and Stripe integration" data-id="element-2698" />
        </div>
        {isLoadingCompanies ? <div className="p-12 text-center" data-id="element-2699">
            <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" data-id="element-2700"></div>
            <p className="mt-4 text-sm text-gray-500" data-id="element-2701">Loading companies...</p>
          </div> : companiesError ? <div className="p-12 text-center" data-id="element-2702">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4" data-id="element-2703">
              <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-2704" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-2705">
              Failed to load companies
            </p>
            <p className="text-sm text-gray-500 mb-4" data-id="element-2706">{companiesError}</p>
            <Button size="sm" onClick={() => fetchCompanies()} data-id="element-2707">
              Retry
            </Button>
          </div> : <Table columns={companyColumns} data={companies} keyExtractor={c => c.id} emptyMessage="No companies found" data-id="element-2708" />}
      </Card>

      <Card padding="none" data-id="element-2709">
        <div className="p-6 border-b border-gray-200" data-id="element-2710">
          <CardHeader title="Terms & Conditions" subtitle="Manage terms and conditions templates for quotes" action={<Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2713" />} onClick={openCreateTermsModal} data-id="element-2712">
                Add Terms
              </Button>} data-id="element-2711" />
        </div>
        <Table columns={termsColumns} data={companyTerms} keyExtractor={t => t.id} emptyMessage="No terms and conditions created yet" data-id="element-2714" />
      </Card>

      <Card data-id="element-2715">
        <CardHeader title="System Information" data-id="element-2716" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-id="element-2717">
          <div data-id="element-2718">
            <span className="text-gray-500" data-id="element-2719">Version</span>
            <p className="font-medium text-gray-900" data-id="element-2720">1.0.0</p>
          </div>
          <div data-id="element-2721">
            <span className="text-gray-500" data-id="element-2722">Environment</span>
            <p className="font-medium text-gray-900" data-id="element-2723">Production</p>
          </div>
          <div data-id="element-2724">
            <span className="text-gray-500" data-id="element-2725">Stripe Fee</span>
            <p className="font-medium text-gray-900" data-id="element-2726">2.9% + $0.30</p>
          </div>
          <div data-id="element-2727">
            <span className="text-gray-500" data-id="element-2728">Data Storage</span>
            <p className="font-medium text-gray-900" data-id="element-2729">API Database</p>
          </div>
        </div>
      </Card>

      <Modal isOpen={isTermsModalOpen} onClose={() => setIsTermsModalOpen(false)} title={editingTerms ? 'Edit Terms & Conditions' : 'Add Terms & Conditions'} size="lg" data-id="element-2730">
        <div className="space-y-4" data-id="element-2731">
          <Input label="Name" value={termsName} onChange={e => setTermsName(e.target.value)} error={termsErrors.name} placeholder="e.g., Standard Terms, Service Agreement" data-id="element-2732" />
          <TextArea label="Content" value={termsContent} onChange={e => setTermsContent(e.target.value)} error={termsErrors.content} placeholder="Enter your terms and conditions..." className="min-h-[200px]" helperText="You can use basic HTML formatting" data-id="element-2733" />
          <Checkbox label="Set as default terms" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} data-id="element-2734" />
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-2735">
            <Button variant="secondary" onClick={() => setIsTermsModalOpen(false)} data-id="element-2736">
              Cancel
            </Button>
            <Button onClick={handleSaveTerms} isLoading={isLoading} data-id="element-2737">
              {editingTerms ? 'Save Changes' : 'Add Terms'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)} title="Delete Terms & Conditions" size="sm" data-id="element-2738">
        <p className="text-gray-600 mb-6" data-id="element-2739">
          Are you sure you want to delete these terms? This action cannot be
          undone.
        </p>
        <div className="flex justify-end gap-3" data-id="element-2740">
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)} data-id="element-2741">
            Cancel
          </Button>
          <Button variant="danger" onClick={() => deleteConfirmId && handleDeleteTerms(deleteConfirmId)} isLoading={isLoading} data-id="element-2742">
            Delete
          </Button>
        </div>
      </Modal>
    </div>;
}